import { useState } from 'react';
import CubeService from '../../../../services/cube';

export const useCubeManagement = () => {
  const [cubes, setCubes] = useState([]);
  const [cubeSaveLoading, setCubeSaveLoading] = useState(false);
  const [cubeSaveResult, setCubeSaveResult] = useState(null);
  const [loadedCubes, setLoadedCubes] = useState([]);

  const handleAddCube = () => {
    const newCube = {
      id: Date.now(), // Simple ID generation
      name: '',
      dimensions: []
    };
    setCubes([...cubes, newCube]);
  };

  const handleRemoveCube = (cubeId) => {
    setCubes(cubes.filter(cube => cube.id !== cubeId));
  };

  const handleCubeNameChange = (cubeId, newName) => {
    setCubes(cubes.map(cube => 
      cube.id === cubeId ? { ...cube, name: newName } : cube
    ));
  };

  const handleDimensionToggle = (cubeId, dimension) => {
    setCubes(cubes.map(cube => {
      if (cube.id === cubeId) {
        const isSelected = cube.dimensions.includes(dimension);
        return {
          ...cube,
          dimensions: isSelected 
            ? cube.dimensions.filter(d => d !== dimension)
            : [...cube.dimensions, dimension]
        };
      }
      return cube;
    }));
  };

  const handleSaveCubes = async (createdSourceId, onSuccess) => {
    // Validate that we have cubes to save and a source ID
    if (!createdSourceId) {
      setCubeSaveResult({
        success: false,
        message: "No source ID available. Please complete the previous steps first."
      });
      return;
    }

    if (cubes.length === 0) {
      setCubeSaveResult({
        success: false,
        message: "No cubes to save. Please add at least one cube."
      });
      return;
    }

    // Validate that all cubes have names and dimensions
    const invalidCubes = cubes.filter(cube => !cube.name || cube.dimensions.length === 0);
    if (invalidCubes.length > 0) {
      setCubeSaveResult({
        success: false,
        message: "All cubes must have a name and at least one dimension selected."
      });
      return;
    }

    setCubeSaveLoading(true);
    setCubeSaveResult(null);

    try {
      const requestData = {
        source_id: createdSourceId,
        cubes: cubes.map(cube => {
          const cubeData = {
            cube_name: cube.name,
            dimension_names: cube.dimensions
          };
          
          // Include ID if this is an existing cube
          if (cube.id && typeof cube.id === 'number' && cube.id < 1000000000000) {
            cubeData.cube_id = cube.id;
          }
          
          return cubeData;
        })
      };

      console.log('Saving cubes with request data:', requestData);
      const result = await CubeService.bulkSaveCubes(requestData);
      console.log('Bulk save result:', result);
      
      setCubeSaveResult(result);

      const totalOperations = (result.created_cubes?.length || 0) + 
                             (result.updated_cubes?.length || 0) + 
                             (result.deleted_cube_ids?.length || 0);
      
      if (result.success || totalOperations > 0) {
        const messageParts = [];
        if (result.created_cubes && result.created_cubes.length > 0) {
          messageParts.push(`Created ${result.created_cubes.length} cube(s)`);
        }
        if (result.updated_cubes && result.updated_cubes.length > 0) {
          messageParts.push(`Updated ${result.updated_cubes.length} cube(s)`);
        }
        if (result.deleted_cube_ids && result.deleted_cube_ids.length > 0) {
          messageParts.push(`Deleted ${result.deleted_cube_ids.length} cube(s)`);
        }
        
        const successMessage = messageParts.length > 0 
          ? `Successfully ${messageParts.join(', ')}`
          : 'Cubes saved successfully!';
        
        window.alert(successMessage);
        
        // Call success callback
        if (onSuccess) onSuccess();
      } else if (result.errors && result.errors.length > 0) {
        const message = result.message || `Errors occurred: ${result.errors.join(', ')}`;
        window.alert(message);
      }

    } catch (error) {
      console.error('Error saving cubes:', error);
      
      let errorMessage = "Failed to save cubes";
      let errorDetail = null;
      let errors = [];
      
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        if (errorData.errors && Array.isArray(errorData.errors)) {
          errors = errorData.errors;
          errorMessage = errorData.message || "Some errors occurred";
        } else if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorDetail = errorData.detail;
          } else if (typeof errorData.detail === 'object' && errorData.detail.details) {
            errorDetail = errorData.detail.details;
          }
        }
        
        if (errorDetail && errorDetail !== "Unknown error occurred") {
          errorMessage = errorDetail;
          errorDetail = null;
        }
      }
      
      setCubeSaveResult({
        success: false,
        message: errorMessage,
        detail: errorDetail,
        errors: errors
      });
      
      if (errors.length > 0) {
        window.alert(`${errorMessage}\n\nErrors:\n${errors.join('\n')}`);
      } else {
        window.alert(errorMessage);
      }
    } finally {
      setCubeSaveLoading(false);
    }
  };

  return {
    cubes,
    setCubes,
    cubeSaveLoading,
    cubeSaveResult,
    loadedCubes,
    setLoadedCubes,
    handleAddCube,
    handleRemoveCube,
    handleCubeNameChange,
    handleDimensionToggle,
    handleSaveCubes,
    setCubeSaveResult
  };
};

